import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

import { BugReportModal } from '../components/BugReportModal';
import { registerBugReporter } from '../lib/toast-error';

export interface BugReportPrefill {
  title?: string;
  description?: string;
  errorRequestId?: string;
  errorMessage?: string;
}

export interface BugReportContextValue {
  open: (prefill?: BugReportPrefill) => void;
}

const BugReportContext = createContext<BugReportContextValue | null>(null);

export interface BugReportProviderProps {
  children: ReactNode;
}

export function BugReportProvider({ children }: BugReportProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState<BugReportPrefill | null>(null);

  const open = useCallback((p?: BugReportPrefill) => {
    setPrefill(p ?? null);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setPrefill(null);
  }, []);

  useEffect(() => {
    registerBugReporter(open);
    return () => registerBugReporter(null);
  }, [open]);

  return (
    <BugReportContext.Provider value={{ open }}>
      {children}
      <BugReportModal isOpen={isOpen} onClose={close} prefill={prefill} />
    </BugReportContext.Provider>
  );
}

export function useBugReport(): BugReportContextValue {
  const ctx = useContext(BugReportContext);
  if (!ctx) {
    throw new Error('useBugReport must be used inside BugReportProvider');
  }
  return ctx;
}
